import React, { useState, useEffect, useContext } from "react";
import {
  LoadingOverlay,
  Box,
  Container,
  NativeSelect,
  Alert,
  Select,
  Text,
  TextInput,
  Button,
  Group,
  Stack,
  Center,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMediaQuery } from "@mantine/hooks";
import { DataContext } from "../../../context/DataContext";
import {
  IconShieldCheck,
  IconShieldCancel,
  IconPhoneCheck,
} from "@tabler/icons-react";
import BasicDataCollectionForm from "../../../components/basic-data-collection-form";
import { validateDob } from "../../../utils/validateDob";
import useLocale from "../../../hooks/useLocale";

export default function IDMatchLegacy() {
  const { text } = useContext(DataContext);
  const locale = useLocale("coppa");

  // Form configuration for COPPA verification
  const formConfig = {
    fields: [
      {
        name: "fn",
        label: locale?.general?.firstName || text.general.firstName,
        initialValue: "",
        validation: (value) => {
          // Check minimum length
          if (value.length < 2) {
            return locale?.general?.firstNameError || text.general.firstNameError;
          }

          // Check for numbers in name
          if (/\d/.test(value)) {
            return locale?.validation?.firstNameNoNumbers || "First name cannot contain numbers";
          }

          return null;
        },
      },
      {
        name: "ln",
        label: locale?.general?.lastName || text.general.lastName,
        initialValue: "",
        validation: (value) => {
          // Check minimum length
          if (value.length < 2) {
            return locale?.general?.lastNameError || text.general.lastNameError;
          }

          // Check for numbers in name
          if (/\d/.test(value)) {
            return locale?.validation?.lastNameNoNumbers || "Last name cannot contain numbers";
          }

          return null;
        },
      },
      {
        name: "dob",
        label: locale?.general?.dateOfBirth || text.general.DateOfBirth,
        type: "date",
        validation: (value) =>
          validateDob(value, locale?.general?.dateOfBirthError || text.general.DateOfBirthError || "Invalid Date of Birth"),
      },
      {
        name: "ssn",
        label: locale?.general?.lastFourSSN || text.general.lastFourSSN,
        initialValue: "",
        // Use our custom masked input instead of password or regular input
        isMaskedInput: true,
        type: "tel", // tel is better than number for this case as it preserves leading zeros
        maxLength: 4,
        pattern: "[0-9]*", // Forces numeric input
        inputMode: "numeric", // Shows numeric keyboard (note: camelCase for React)
        validation: (value) => {
          // Check for non-numeric characters
          if (/[^0-9]/.test(value)) {
            return locale?.validation?.ssnOnlyNumbers || "SSN must contain only numbers";
          }
          // Check length
          if (value.length !== 4) {
            return locale?.general?.lastFourSSNError || text.general.lastFourSSNError;
          }
          return null;
        },
      },
    ],
  };

  return (
    <BasicDataCollectionForm
      config={formConfig}
      title={locale?.coppa?.title || text.coppa.title}
      subtitle={locale?.coppa?.subTitle || text.coppa.subTitle}
      buttonText={locale?.coppa?.button || text.coppa.button}
      loadingText={locale?.form?.verifying}
      errorText={locale?.form?.somethingWentWrong}
      icon={<IconShieldCheck size={40} stroke={1.5} />}
      componentKey={"idmatch/coppa"}
      endpoint="/v3/stable/api/frontend/idmatchCoppa"
      reAttempts={2}
    />
  );
}
